import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { buildAcceptUrl } from '../utils/inviteUrl';
import { useAdminScopeInfo } from '../hooks/useAdminScope';
import {
  allowNoDepartment,
  assignableRoles,
  departmentOptionsFor,
  isProtectedFromRestricted,
} from '../utils/adminAccess';
import {
  adminDepartmentsApi,
  adminUsersApi,
  RequestError,
  type AdminUserResponse,
  type DepartmentResponse,
  type InviteResendResponse,
} from '../api/client';


type EditRow = {
  display_name: string;
  role: string;
  department_id: string;
};

const ROLE_OPTIONS = ['employee', 'admin', 'healthcare'] as const;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

function IconSaveRow() {
  return (
    <svg
      className="admin-icon-btn__glyph"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );
}

function IconPasswordReset() {
  return (
    <svg
      className="admin-icon-btn__glyph"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** BE 未対応 API（delete / exportCsv）のエラーを「未実装」として検出する */
function isNotImplementedError(err: unknown): boolean {
  if (err instanceof RequestError) {
    return err.status === 404 || err.status === 405 || err.status === 501;
  }
  return false;
}

/** CSV Blob をブラウザでダウンロードさせる */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** ADM-S06 ユーザー管理（API 連携） */
export default function AdminUsers() {
  const { t } = useTranslation();
  const roleOptionLabel = (role: string) =>
    t(`admin.usersRoleOption.${role}`, { defaultValue: role });
  const statusBadgeLabel = (status: string) =>
    t(`admin.usersStatusOption.${status}`, { defaultValue: status });
  const { getToken } = useAuth();
  const [list, setList] = useState<AdminUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDisplayName, setInviteDisplayName] = useState('');
  const [inviteRole, setInviteRole] = useState('employee');
  /** 全社管理者か拠点管理者か（#100）。拠点管理者には操作できないものを出さない */
  const scopeInfo = useAdminScopeInfo();
  const adminScope = scopeInfo.status;
  /**
   * 利用者の削除を BE が止めている間は「完全削除」を出さず、止めていることを案内する（課題 #71 の案 2）。
   * 閲覧範囲を読み込んでいる間も出さない（出した直後に消えないように）
   */
  const deletionStopped = scopeInfo.userDeletion === 'stopped';
  const showDelete = adminScope !== 'loading' && !deletionStopped;
  const [inviteDays, setInviteDays] = useState('7');
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  /**
   * 招待の受諾 URL（課題 #54）。BE が invite_token を返したときだけ持つ。
   * メールが届かない・見られない方へ別の経路で渡すために表示する。
   * リンクそのものがアカウント設定の権限になるため、扱いは注意が必要。
   */
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteUrlCopied, setInviteUrlCopied] = useState(false);

  /** 招待の再送（課題 #54） */
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendResult, setResendResult] = useState<InviteResendResponse | null>(null);
  /**
   * 受諾 URL を応答に含めるか（課題 #54 / BE の include_token）。
   * 既定は off。true にした実行は監査ログに別イベントとして残るため、
   * 「メールを確認できない方に直接渡す」と決めたときだけ使う。
   */
  const [resendIncludeToken, setResendIncludeToken] = useState(false);
  const [resendUrlCopied, setResendUrlCopied] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [editRows, setEditRows] = useState<Record<string, EditRow>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [rowNotice, setRowNotice] = useState<Record<string, string>>({});
  // 「完全削除」確認ダイアログ: 対象ユーザー + 入力したメール（確認用）
  const [deleteTarget, setDeleteTarget] = useState<AdminUserResponse | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // 一括 CSV ダウンロードの選択状態と進捗
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  // 一覧（名簿）CSV のダウンロード状態。1 名分の開示請求用エクスポートとは別物
  const [rosterExporting, setRosterExporting] = useState(false);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [rosterNotice, setRosterNotice] = useState<string | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  // 部署マスタ（GET /v1/admin/departments）。取得失敗時は null のままにしてフォールバック表示
  const [departments, setDepartments] = useState<DepartmentResponse[] | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    adminUsersApi
      .list(token, { limit: 100 })
      .then((rows) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        setList(safeRows);
        setEditRows(
          safeRows.reduce<Record<string, EditRow>>((acc, row) => {
            acc[row.id] = {
              display_name: row.display_name ?? '',
              role: row.role ?? 'employee',
              department_id: row.department_id ?? '',
            };
            return acc;
          }, {})
        );
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  // 部署マスタ取得（成功時のみ state を更新。失敗時は null のままでテキスト入力フォールバック）
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    adminDepartmentsApi
      .list(token)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setDepartments(rows);
      })
      .catch(() => {
        // 取得失敗時はフォールバックで従来のテキスト入力 + UUID 警告を表示する
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const sortedList = useMemo(
    () => [...list].sort((a, b) => (a.email ?? '').localeCompare(b.email ?? '', 'ja-JP')),
    [list]
  );
  /** 一括操作で選べる行（拠点管理者は、管理者・産業保健職の行を選べない。#100） */
  const selectableList = useMemo(
    () => sortedList.filter((r) => !isProtectedFromRestricted(r.role, adminScope)),
    [sortedList, adminScope]
  );

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    setInviteError(null);
    setInviteMessage(null);
    const daysRaw = inviteDays.trim();
    let expiresInDays: number | undefined;
    if (daysRaw !== '') {
      const n = Number(daysRaw);
      if (!Number.isInteger(n) || n < 1 || n > 30) {
        setInviteError(t('admin.usersInviteExpiresInvalid'));
        return;
      }
      expiresInDays = n;
    }
    // BE が 409 + error_code="EMAIL_ALREADY_INVITED" を即時返却するため、
    // フロント側のローカル list 重複チェックは行わず、BE エラーをそのまま表示する
    adminUsersApi
      .invite(
        {
          email: inviteEmail.trim(),
          ...(inviteDisplayName.trim() ? { display_name: inviteDisplayName.trim() } : {}),
          role: inviteRole,
          expires_in_days: expiresInDays,
        },
        token
      )
      .then((res) => {
        setInviteMessage(t('admin.usersInviteSent'));
        // BE が invite_token を返した場合のみ受諾 URL を出す（返らないことがある）
        setInviteUrl(res?.invite_token ? buildAcceptUrl(res.invite_token) : null);
        setInviteUrlCopied(false);
        setInviteEmail('');
        setInviteDisplayName('');
        setInviteRole('employee');
        setInviteDays('7');
        load();
      })
      .catch((err) => {
        setInviteError(err instanceof Error ? err.message : t('common.error'));
      });
  };

  /**
   * 招待を再送する（課題 #54）。
   * 再送すると元の招待は無効になるため、前に渡したリンクは使えなくなる。
   */
  const handleResendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    const email = resendEmail.trim();
    if (!token || !email || resending) return;
    setResendError(null);
    setResendResult(null);
    setResending(true);
    adminUsersApi
      .resendInvite({ email, include_token: resendIncludeToken }, token)
      .then((res) => {
        setResendResult(res);
        setResendUrlCopied(false);
        setResendEmail('');
        load();
      })
      .catch((err) => {
        setResendError(err instanceof Error ? err.message : t('common.error'));
      })
      .finally(() => setResending(false));
  };

  const clearRowMessages = (userId: string) => {
    setRowError((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    setRowNotice((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const handleUpdate = (userId: string) => {
    const token = getToken();
    if (!token) return;
    const edit = editRows[userId];
    if (!edit) return;
    clearRowMessages(userId);
    setSavingUserId(userId);
    adminUsersApi
      .update(
        userId,
        {
          // 氏名は空欄なら空文字を送って消す（BE: 空文字で消す／未指定は変更なし）
          display_name: edit.display_name.trim(),
          role: edit.role,
          /*
           * 空欄は「所属なしに戻す」意図なので **空文字** を送る（課題 #51 / BE 回答 2026-09-09）。
           *
           * BE の update_user は `if body.department_id is not None:` で分岐するため、
           *   - undefined … JSON でキーごと消える → 変更なし
           *   - null      … None になり分岐に入らない → 変更なし
           *   - ""        … 分岐に入り `body.department_id or None` で未割当が保存される ← これだけ解除が効く
           * openapi 上は string | null だが、実装は「キーの有無」と null を区別できないため
           * 空文字が唯一の解除経路になっている。
           */
          department_id: edit.department_id.trim(),
        },
        token
      )
      .then(() => load())
      .catch((err) => {
        // BE が 422 + error_code="INVALID_DEPARTMENT_ID" を返した場合は具体的メッセージで案内する
        let message: string;
        if (err instanceof RequestError && err.errorCode === 'INVALID_DEPARTMENT_ID') {
          message = t('admin.usersInvalidDepartmentIdError');
        } else if (err instanceof Error) {
          message = err.message;
        } else {
          message = t('common.error');
        }
        setRowError((prev) => ({
          ...prev,
          [userId]: message,
        }));
      })
      .finally(() => setSavingUserId(null));
  };

  /**
   * ソフト削除（無効化）: status を反転させる。active ⇄ inactive。
   * 既存の PUT /v1/admin/users/{id} を使うので BE 変更は不要
   */
  const handleToggleActive = (row: AdminUserResponse) => {
    const token = getToken();
    if (!token) return;
    const nextStatus = row.status === 'inactive' ? 'active' : 'inactive';
    const confirmMsg =
      nextStatus === 'inactive'
        ? t('admin.usersDeactivateConfirm', { email: row.email })
        : t('admin.usersReactivateConfirm', { email: row.email });
    if (!window.confirm(confirmMsg)) return;
    clearRowMessages(row.id);
    setSavingUserId(row.id);
    adminUsersApi
      .update(row.id, { status: nextStatus }, token)
      .then(() => {
        setRowNotice((prev) => ({
          ...prev,
          [row.id]:
            nextStatus === 'inactive'
              ? t('admin.usersDeactivated')
              : t('admin.usersReactivated'),
        }));
        load();
      })
      .catch((err) => {
        setRowError((prev) => ({
          ...prev,
          [row.id]: err instanceof Error ? err.message : t('common.error'),
        }));
      })
      .finally(() => setSavingUserId(null));
  };

  const openDeleteDialog = (row: AdminUserResponse) => {
    setDeleteTarget(row);
    setDeleteConfirmEmail('');
    setDeleteError(null);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirmEmail('');
    setDeleteError(null);
  };

  const handleHardDelete = async () => {
    if (!deleteTarget) return;
    const token = getToken();
    if (!token) return;
    if (deleteConfirmEmail.trim() !== deleteTarget.email) {
      setDeleteError(t('admin.usersDeleteConfirmEmailMismatch'));
      return;
    }
    setDeleteError(null);
    setDeleting(true);
    try {
      await adminUsersApi.delete(deleteTarget.id, token);
      setDeleteTarget(null);
      setDeleteConfirmEmail('');
      load();
    } catch (err) {
      if (isNotImplementedError(err)) {
        setDeleteError(t('admin.usersDeleteNotImplemented'));
      } else {
        setDeleteError(err instanceof Error ? err.message : t('common.error'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === selectableList.length) return new Set();
      return new Set(selectableList.map((r) => r.id));
    });
  };

  /**
   * 選択されたユーザーごとに CSV を順次ダウンロードする。
   * BE のレート制限（24h x 5/ユーザー）とブラウザの多重ダウンロード制限を考慮し、
   * sequential に処理して進捗を表示する。各ユーザーごとに別ファイルとして保存される
   */
  /**
   * ユーザー一覧（名簿）を 1 ファイルの CSV でダウンロードする（BE 2026-09-02 新設）。
   * 選択したユーザーを 1 名ずつ落とす一括エクスポートとは用途が異なり、
   * 文字起こしを含まず 1 リクエストで完結する。
   */
  const handleRosterExport = async () => {
    const token = getToken();
    if (!token) return;
    if (!window.confirm(t('admin.usersRosterExportConfirm'))) return;
    setRosterError(null);
    setRosterNotice(null);
    setRosterExporting(true);
    try {
      const blob = await adminUsersApi.exportRosterCsv(token);
      const d = new Date();
      const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      downloadBlob(blob, `users_${ymd}.csv`);
      setRosterNotice(t('admin.usersRosterExportDone'));
    } catch (err) {
      if (isNotImplementedError(err)) {
        setRosterError(t('admin.usersExportNotImplemented'));
      } else {
        setRosterError(err instanceof Error ? err.message : t('common.error'));
      }
    } finally {
      setRosterExporting(false);
    }
  };

  const handleBulkExportCsv = async () => {
    const token = getToken();
    if (!token || selectedIds.size === 0) return;
    const targets = sortedList.filter((r) => selectedIds.has(r.id));
    if (!window.confirm(t('admin.usersBulkExportConfirm', { count: targets.length }))) {
      return;
    }
    setBulkError(null);
    setBulkProgress({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      const row = targets[i];
      try {
        const blob = await adminUsersApi.exportCsv(row.id, token);
        const filename = `user_${row.email.replace(/[^a-zA-Z0-9_.-]/g, '_')}_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`;
        downloadBlob(blob, filename);
      } catch (err) {
        const message =
          err instanceof RequestError && isNotImplementedError(err)
            ? t('admin.usersExportNotImplemented')
            : err instanceof Error
              ? err.message
              : t('common.error');
        setBulkError(t('admin.usersBulkExportPartialFail', { email: row.email, message }));
        setBulkProgress(null);
        return;
      }
      setBulkProgress({ done: i + 1, total: targets.length });
      // ブラウザ側のダウンロード処理を詰まらせないよう 200ms 間隔を空ける
      if (i < targets.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }
    setBulkProgress(null);
    setSelectedIds(new Set());
  };

  const handleExportCsv = async (row: AdminUserResponse) => {
    const token = getToken();
    if (!token) return;
    // transcript_text を含む管理者特例エクスポートのため、明示的な確認を取る
    if (!window.confirm(t('admin.usersExportConfirm', { email: row.email }))) {
      return;
    }
    clearRowMessages(row.id);
    setSavingUserId(row.id);
    try {
      const blob = await adminUsersApi.exportCsv(row.id, token);
      const filename = `user_${row.email.replace(/[^a-zA-Z0-9_.-]/g, '_')}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      downloadBlob(blob, filename);
      setRowNotice((prev) => ({ ...prev, [row.id]: t('admin.usersExportDone') }));
    } catch (err) {
      if (isNotImplementedError(err)) {
        setRowError((prev) => ({
          ...prev,
          [row.id]: t('admin.usersExportNotImplemented'),
        }));
      } else {
        setRowError((prev) => ({
          ...prev,
          [row.id]: err instanceof Error ? err.message : t('common.error'),
        }));
      }
    } finally {
      setSavingUserId(null);
    }
  };

  const handleReset = (userId: string) => {
    const token = getToken();
    if (!token) return;
    clearRowMessages(userId);
    setSavingUserId(userId);
    adminUsersApi
      .resetPassword(userId, token)
      .then((res) => {
        setRowNotice((prev) => ({
          ...prev,
          [userId]: t('admin.usersResetEmail.sent', {
            expires: new Date(res.expires_at).toLocaleString('ja-JP'),
          }),
        }));
      })
      .catch((err) => {
        let message: string;
        if (err instanceof RequestError && err.errorCode === 'EMAIL_NOT_CONFIGURED') {
          message = t('admin.usersResetEmail.notConfigured');
        } else if (err instanceof RequestError && err.errorCode === 'EMAIL_SEND_FAILED') {
          message = t('admin.usersResetEmail.sendFailed');
        } else if (err instanceof Error) {
          message = err.message;
        } else {
          message = t('common.error');
        }
        setRowError((prev) => ({
          ...prev,
          [userId]: message,
        }));
      })
      .finally(() => setSavingUserId(null));
  };

  return (
    <div className="page admin-users-page">
      <h1>{t('admin.usersManage')}</h1>
      <p className="admin-users-import-link">
        <Link to="/admin/users/import">{t('admin.usersImport.link')}</Link>
        {' ／ '}
        <Link to="/admin/users/bulk-department">{t('admin.usersBulkDepartment.link')}</Link>
      </p>

      <form onSubmit={handleInvite} className="admin-card admin-form">
        <h3>{t('admin.usersInvite')}</h3>
        <div className="admin-form-group">
          <label htmlFor="invite-email">{t('auth.email')}</label>
          <input id="invite-email"
            type="email"
            required
            data-testid="invite-email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="new-user@example.com"
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="invite-display-name">{t('admin.usersDisplayNameOptional')}</label>
          <input
            id="invite-display-name"
            type="text"
            maxLength={100}
            data-testid="invite-display-name"
            value={inviteDisplayName}
            onChange={(e) => setInviteDisplayName(e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label htmlFor="admin-usersRole">{t('admin.usersRole')}</label>
          <select id="admin-usersRole" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
            {/* 拠点管理者が招待できるのは従業員だけ（#100。BE も管理者・産業保健職の招待を 403 で止める） */}
            {assignableRoles(ROLE_OPTIONS, adminScope).map((role) => (
              <option key={role} value={role}>{roleOptionLabel(role)}</option>
            ))}
          </select>
        </div>
        <div className="admin-form-group">
          <label htmlFor="admin-usersInviteExpires">{t('admin.usersInviteExpires')}</label>
          <input id="admin-usersInviteExpires"
            type="number"
            min={1}
            max={30}
            value={inviteDays}
            onChange={(e) => setInviteDays(e.target.value)}
          />
          <p className="admin-form-hint">{t('admin.usersInviteExpiresHint')}</p>
        </div>
        {inviteError && <p className="admin-form-error">{inviteError}</p>}
        {inviteMessage && <p className="admin-form-success">{inviteMessage}</p>}
        {/*
          受諾 URL（課題 #54）。メールが届かない・見られない方へ別の経路で渡すために出す。
          このリンクを持っていればそのアカウントを設定できてしまうため、扱いは注意が要る。
        */}
        {inviteUrl && (
          <div className="admin-invite-url" data-testid="invite-url">
            <p className="admin-form-hint">{t('admin.usersInviteUrl.hint')}</p>
            <code className="admin-invite-url-value">{inviteUrl}</code>
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={() => {
                navigator.clipboard?.writeText(inviteUrl).then(
                  () => setInviteUrlCopied(true),
                  () => setInviteUrlCopied(false)
                );
              }}
            >
              {inviteUrlCopied ? t('admin.usersInviteUrl.copied') : t('admin.usersInviteUrl.copy')}
            </button>
            <p className="admin-form-warning">{t('admin.usersInviteUrl.caution')}</p>
            <p className="admin-form-warning">{t('admin.usersInviteUrl.resendInvalidates')}</p>
          </div>
        )}
        <button type="submit" className="admin-btn-primary">{t('admin.usersInviteSubmit')}</button>
      </form>

      {/*
        招待の再送（課題 #54）。有効な招待が残っていると通常の招待は 409 になるため、
        「メールが届かない・見失った」に対してはこちらを使う。
      */}
      <form onSubmit={handleResendInvite} className="admin-card admin-form">
        <h3>{t('admin.usersResend.title')}</h3>
        <p className="admin-form-hint">{t('admin.usersResend.intro')}</p>
        <div className="admin-form-group">
          <label htmlFor="resend-email">{t('auth.email')}</label>
          <input id="resend-email"
            type="email"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            required
            data-testid="resend-email"
          />
        </div>
        {/*
          既定では URL を出さない（BE の include_token も既定 false）。
          送信が成功しているのに取り出せる状態を常態にしないための線引きで、
          true にした実行は監査ログに別イベントとして残る。
        */}
        <div className="admin-form-group">
          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={resendIncludeToken}
              onChange={(e) => setResendIncludeToken(e.target.checked)}
              data-testid="resend-include-token"
            />
            {t('admin.usersResend.includeToken')}
          </label>
          <p className="admin-form-hint">{t('admin.usersResend.includeTokenHint')}</p>
        </div>
        <p className="admin-form-warning">{t('admin.usersResend.invalidatesPrevious')}</p>
        {resendError && (
          <p className="admin-form-error" data-testid="resend-error">
            {resendError}
          </p>
        )}
        {resendResult && (
          <div data-testid="resend-result">
            <p className="admin-form-success">
              {resendResult.email_sent
                ? t('admin.usersResend.sent', { email: resendResult.email })
                : t('admin.usersResend.notSent', { email: resendResult.email })}
            </p>
            {/* 送信に失敗した理由が返るなら隠さない。届かない原因の切り分けに要る */}
            {resendResult.email_error && (
              <p className="admin-form-error">{resendResult.email_error}</p>
            )}
            <p className="admin-form-hint">
              {t('admin.usersResend.expiresAt', { at: resendResult.expires_at })}
            </p>
            {/*
              受諾 URL。BE は base URL 未設定の環境では invite_url を null で返すため、
              トークンがあれば管理画面自身のオリジンで組み立てる。
              こうしておくと、BE 側の base URL 設定に依存せず本番でも正しい URL になる。
            */}
            {(() => {
              const url =
                resendResult.invite_url ||
                (resendResult.invite_token ? buildAcceptUrl(resendResult.invite_token) : null);
              if (!url) return null;
              return (
                <div className="admin-invite-url" data-testid="resend-url">
                  <p className="admin-form-hint">{t('admin.usersInviteUrl.hint')}</p>
                  <code className="admin-invite-url-value">{url}</code>
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={() => {
                      navigator.clipboard?.writeText(url).then(
                        () => setResendUrlCopied(true),
                        () => setResendUrlCopied(false)
                      );
                    }}
                  >
                    {resendUrlCopied
                      ? t('admin.usersInviteUrl.copied')
                      : t('admin.usersInviteUrl.copy')}
                  </button>
                  <p className="admin-form-warning">{t('admin.usersInviteUrl.caution')}</p>
                  {/*
                    渡したあとにもう一度再送すると、渡した URL も失効する。
                    URL のすぐ横に置かないと、再送フォーム上部の注意書きは読み飛ばされる。
                  */}
                  <p className="admin-form-warning">{t('admin.usersInviteUrl.resendInvalidates')}</p>
                </div>
              );
            })()}
          </div>
        )}
        <button
          type="submit"
          className="admin-btn-primary"
          disabled={resending || !resendEmail.trim()}
          data-testid="resend-submit"
        >
          {resending ? t('common.loading') : t('admin.usersResend.submit')}
        </button>
      </form>

      <div className="admin-card">
        <h3>{t('admin.usersList')}</h3>
        <div className="admin-users-privacy-notice" role="note">
          <strong>{t('admin.usersPrivacyNotice.title')}</strong>
          <ul>
            <li>{t('admin.usersPrivacyNotice.csv')}</li>
            <li>{deletionStopped ? t('admin.usersDeleteStopped') : t('admin.usersPrivacyNotice.delete')}</li>
            <li>{t('admin.usersPrivacyNotice.audit')}</li>
          </ul>
        </div>

        {/* 一覧（名簿）CSV。開示請求用の 1 名分エクスポートと取り違えないよう用途を明記する */}
        <div className="admin-users-roster-bar">
          <div className="admin-users-roster-text">
            <span className="admin-users-roster-title">{t('admin.usersRosterExportTitle')}</span>
            <span className="admin-form-hint">{t('admin.usersRosterExportHint')}</span>
          </div>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={handleRosterExport}
            disabled={rosterExporting}
          >
            {rosterExporting ? t('common.loading') : t('admin.usersRosterExport')}
          </button>
        </div>
        {rosterError && <p className="admin-form-error">{rosterError}</p>}
        {rosterNotice && <p className="admin-form-success">{rosterNotice}</p>}

        <div className="admin-users-bulk-bar">
          <span className="admin-users-bulk-count">
            {t('admin.usersBulkSelected', { count: selectedIds.size, total: sortedList.length })}
          </span>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={handleBulkExportCsv}
            disabled={selectedIds.size === 0 || bulkProgress != null}
          >
            {bulkProgress
              ? t('admin.usersBulkExporting', {
                  done: bulkProgress.done,
                  total: bulkProgress.total,
                })
              : t('admin.usersBulkExport')}
          </button>
        </div>
        {bulkError && <p className="admin-form-error">{bulkError}</p>}

        {error && <p className="admin-form-error">{error}</p>}
        {loading && <p>{t('common.loading')}</p>}
        {!loading && !error && (
          sortedList.length === 0 ? (
            <p className="admin-empty">{t('admin.usersEmpty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">
                    <input
                      type="checkbox"
                      aria-label={t('admin.usersBulkSelectAll')}
                      checked={selectableList.length > 0 && selectedIds.size === selectableList.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th scope="col">{t('auth.email')}</th>
                  <th scope="col">{t('admin.usersDisplayName')}</th>
                  <th scope="col">{t('admin.usersRole')}</th>
                  <th scope="col">{t('admin.status')}</th>
                  <th scope="col">{t('admin.usersDepartmentId')}</th>
                  <th scope="col">{t('admin.usersConsent.column')}</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {sortedList.map((row) => {
                  const edit = editRows[row.id] ?? {
                    display_name: row.display_name ?? '',
                    role: row.role,
                    department_id: row.department_id ?? '',
                  };
                  const isInactive = row.status === 'inactive';
                  const protectedRow = isProtectedFromRestricted(row.role, adminScope);
                  const selected = selectedIds.has(row.id);
                  return (
                    <tr key={row.id} className={isInactive ? 'admin-users-row-inactive' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={protectedRow}
                          onChange={() => toggleSelect(row.id)}
                          aria-label={t('admin.usersBulkSelectRow', { email: row.email })}
                        />
                      </td>
                      <td>{row.email}</td>
                      <td>
                        <input
                          type="text"
                          maxLength={100}
                          value={edit.display_name}
                          disabled={protectedRow}
                          aria-label={t('admin.usersDisplayNameFor', { email: row.email })}
                          onChange={(e) =>
                            setEditRows((prev) => ({ ...prev, [row.id]: { ...edit, display_name: e.target.value } }))
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={edit.role}
                          disabled={protectedRow}
                          onChange={(e) => setEditRows((prev) => ({ ...prev, [row.id]: { ...edit, role: e.target.value } }))}
                        >
                          {(protectedRow ? [row.role] : assignableRoles(ROLE_OPTIONS, adminScope)).map((role) => (
                            <option key={role} value={role}>{roleOptionLabel(role)}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <span
                          className={`settings-badge admin-users-status-badge admin-users-status-badge--${row.status}`}
                        >
                          {statusBadgeLabel(row.status)}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const trimmed = edit.department_id.trim();
                          // 部署マスタが取得できていて、かつ
                          // 「空欄」または「マスタに存在する ID」のときは <select> ドロップダウンを表示する。
                          // マスタにない ID が既に入っているユーザー編集時はテキスト入力にフォールバックする
                          const knownInMaster =
                            trimmed === '' ||
                            (departments != null &&
                              departments.some((d) => d.id === trimmed));
                          const useSelect =
                            departments != null && departments.length > 0 && knownInMaster;
                          if (useSelect) {
                            return (
                              <select
                                value={edit.department_id}
                                disabled={protectedRow}
                                onChange={(e) =>
                                  setEditRows((prev) => ({
                                    ...prev,
                                    [row.id]: { ...edit, department_id: e.target.value },
                                  }))
                                }
                              >
                                {/* 拠点管理者は「所属なし」へ移せない（#100。BE が 403） */}
                                {(allowNoDepartment(scopeInfo) || trimmed === '') && (
                                  <option value="">
                                    {t('admin.usersDepartmentPlaceholder')}
                                  </option>
                                )}
                                {/*
                                  拠点管理者には閲覧範囲の部署だけを出す（#100。範囲外は BE が 403）。
                                  今の所属が範囲外に見える場合でも、表示が崩れないよう今の所属は残す
                                */}
                                {departments!
                                  .filter(
                                    (d) =>
                                      d.id === trimmed ||
                                      departmentOptionsFor(departments!, scopeInfo).some((o) => o.id === d.id)
                                  )
                                  .map((d) => (
                                    <option key={d.id} value={d.id}>
                                      {d.name}
                                    </option>
                                  ))}
                              </select>
                            );
                          }
                          return (
                            <>
                              <input
                                type="text"
                                value={edit.department_id}
                                disabled={protectedRow}
                                onChange={(e) =>
                                  setEditRows((prev) => ({
                                    ...prev,
                                    [row.id]: { ...edit, department_id: e.target.value },
                                  }))
                                }
                                placeholder={t('admin.usersDepartmentIdPlaceholder')}
                              />
                              {trimmed !== '' && !isValidUuid(trimmed) && (
                                <p className="admin-form-warning admin-users-uuid-warning">
                                  {t('admin.usersDepartmentIdFormatWarn')}
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      {/*
                        同意状態（課題 #57）。granted が null のときは「未取得」ではなく
                        「判定できない」。同意文書が公開されていないだけの状態を
                        「本人が同意していない」と読ませると、対応の向きを誤らせる。
                      */}
                      <td className="admin-users-consent-cell">
                        {(() => {
                          const c = row.consents?.personal_view;
                          if (!c || c.granted == null) {
                            return (
                              <span
                                className="admin-users-consent admin-users-consent--unknown"
                                title={t('admin.usersConsent.unknownHint')}
                                data-testid={`consent-${row.id}`}
                              >
                                {t('admin.usersConsent.unknown')}
                              </span>
                            );
                          }
                          if (c.granted) {
                            return (
                              <span
                                className="admin-users-consent admin-users-consent--granted"
                                data-testid={`consent-${row.id}`}
                              >
                                {t('admin.usersConsent.granted')}
                              </span>
                            );
                          }
                          const missing = (c.missing ?? [])
                            .map((m) => m.label_ja || m.purpose)
                            .filter(Boolean)
                            .join('、');
                          return (
                            <span
                              className="admin-users-consent admin-users-consent--not-granted"
                              title={missing ? `${t('admin.usersConsent.missingTitle')}: ${missing}` : undefined}
                              data-testid={`consent-${row.id}`}
                            >
                              {t('admin.usersConsent.notGranted')}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {/*
                          拠点管理者は、管理者・産業保健職のアカウントを編集・再設定・削除・書き出しできない
                          （#100。BE も 403 で止める）。押して失敗させるより、最初から出さない
                        */}
                        {protectedRow ? (
                          <p className="admin-form-hint" data-testid="user-row-protected">
                            {t('admin.usersProtectedRow')}
                          </p>
                        ) : (
                        <div className="admin-users-actions">
                          <button
                            type="button"
                            className="admin-icon-btn"
                            onClick={() => handleUpdate(row.id)}
                            disabled={savingUserId === row.id}
                            aria-label={t('admin.usersAriaSave')}
                            title={t('admin.usersAriaSave')}
                          >
                            <IconSaveRow />
                          </button>
                          <button
                            type="button"
                            className="admin-icon-btn admin-icon-btn--muted"
                            onClick={() => handleReset(row.id)}
                            disabled={savingUserId === row.id}
                            aria-label={t('admin.usersAriaResetPassword')}
                            title={t('admin.usersAriaResetPassword')}
                          >
                            <IconPasswordReset />
                          </button>
                          <button
                            type="button"
                            className="admin-btn-text"
                            onClick={() => handleExportCsv(row)}
                            disabled={savingUserId === row.id}
                            title={t('admin.usersExportTitle')}
                          >
                            {t('admin.usersExport')}
                          </button>
                          <button
                            type="button"
                            className="admin-btn-text"
                            onClick={() => handleToggleActive(row)}
                            disabled={savingUserId === row.id}
                            title={isInactive ? t('admin.usersReactivateTitle') : t('admin.usersDeactivateTitle')}
                          >
                            {isInactive ? t('admin.usersReactivate') : t('admin.usersDeactivate')}
                          </button>
                          {showDelete && (
                            <button
                              type="button"
                              className="admin-btn-text admin-btn-text-danger"
                              onClick={() => openDeleteDialog(row)}
                              disabled={savingUserId === row.id}
                              title={t('admin.usersDeleteTitle')}
                            >
                              {t('admin.usersDelete')}
                            </button>
                          )}
                        </div>
                        )}
                        {rowError[row.id] && (
                          <p className="admin-form-error admin-users-row-error">{rowError[row.id]}</p>
                        )}
                        {rowNotice[row.id] && (
                          <p className="admin-form-success admin-users-row-notice">{rowNotice[row.id]}</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {deleteTarget && (
        <div className="admin-consent-docs-dialog-backdrop" role="dialog" aria-modal="true">
          <div className="admin-consent-docs-dialog">
            <h3>{t('admin.usersDeleteDialog.title')}</h3>
            <p className="admin-consent-docs-dialog-warning" role="alert">
              {t('admin.usersDeleteDialog.warning')}
            </p>
            <ul className="admin-users-delete-list">
              <li>{t('admin.usersDeleteDialog.bullet1', { email: deleteTarget.email })}</li>
              <li>{t('admin.usersDeleteDialog.bullet2')}</li>
              <li>{t('admin.usersDeleteDialog.bullet3')}</li>
              <li>{t('admin.usersDeleteDialog.bullet4')}</li>
            </ul>
            <div className="admin-form-group">
              <label htmlFor="admin-usersDeleteDialog-confirmEmailLabel">{t('admin.usersDeleteDialog.confirmEmailLabel', { email: deleteTarget.email })}</label>
              <input id="admin-usersDeleteDialog-confirmEmailLabel"
                type="email"
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                placeholder={deleteTarget.email}
                autoComplete="off"
              />
            </div>
            {deleteError && <p className="admin-form-error">{deleteError}</p>}
            <div className="admin-consent-docs-dialog-actions">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={closeDeleteDialog}
                disabled={deleting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn-danger"
                onClick={handleHardDelete}
                disabled={deleting || deleteConfirmEmail.trim() !== deleteTarget.email}
              >
                {deleting ? t('common.loading') : t('admin.usersDeleteDialog.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
